<template>
  <div class="calendar-grid-container">
    <!-- Calendar Header -->
    <div class="calendar-header d-flex justify-content-between align-items-center mb-3">
      <button class="btn btn-outline-secondary btn-sm" @click="$emit('prev-month')">
        <i class="bi bi-chevron-left"></i>
      </button>
      <h5 class="mb-0">{{ currentMonthYear }}</h5>
      <button class="btn btn-outline-secondary btn-sm" @click="$emit('next-month')">
        <i class="bi bi-chevron-right"></i>
      </button>
    </div>

    <!-- Prediction Summary -->
    <div v-if="predictions.nextPeriodDate" class="alert alert-info mb-3">
      <div class="d-flex align-items-center">
        <i class="bi bi-calendar-check me-2"></i>
        <div>
          <strong>Next Period: </strong>
          {{ formatDate(predictions.nextPeriodDate) }}
          <br />
          <small v-if="predictions.ovulationDate">
            <strong>Ovulation: </strong>
            {{ formatDate(predictions.ovulationDate) }}
          </small>
        </div>
      </div>
    </div>

    <!-- Weekday Headers -->
    <div class="calendar-weekdays">
      <div v-for="day in weekdays" :key="day" class="weekday-header">
        {{ day }}
      </div>
    </div>

    <!-- Calendar Days Grid -->
    <div class="calendar-days">
      <div
        v-for="day in calendarDays"
        :key="day.dateString"
        class="calendar-day"
        :class="getDayClass(day)"
        @click="$emit('day-click', day)"
      >
        <span class="day-number">{{ day.day }}</span>
        <div v-if="day.hasRecord" class="record-indicator">
          <i class="bi bi-check-circle-fill text-success"></i>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CalendarGrid',
  props: {
    currentDate: {
      type: Date,
      required: true
    },
    records: {
      type: Array,
      default: () => []
    },
    predictions: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['prev-month', 'next-month', 'day-click'],
  data() {
    return {
      weekdays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    }
  },
  computed: {
    currentMonthYear() {
      return this.currentDate.toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric'
      })
    },
    calendarDays() {
      const year = this.currentDate.getFullYear()
      const month = this.currentDate.getMonth()

      const firstDay = new Date(year, month, 1)
      const lastDay = new Date(year, month + 1, 0)

      const startDate = new Date(firstDay)
      startDate.setDate(startDate.getDate() - firstDay.getDay())

      const endDate = new Date(lastDay)
      const remainingDays = 6 - lastDay.getDay()
      endDate.setDate(endDate.getDate() + remainingDays)

      const days = []
      const currentDay = new Date(startDate)

      while (currentDay <= endDate) {
        const dayData = {
          date: new Date(currentDay),
          day: currentDay.getDate(),
          dateString: currentDay.toISOString().split('T')[0],
          isCurrentMonth: currentDay.getMonth() === month,
          isToday: this.isToday(currentDay),
          hasRecord: this.hasRecordOnDate(currentDay),
          isPeriod: this.isPeriodDay(currentDay),
          isPredictedPeriod: this.isPredictedPeriod(currentDay),
          isOvulation: this.isOvulationDay(currentDay),
          isFertile: this.isFertileDay(currentDay)
        }
        days.push(dayData)
        currentDay.setDate(currentDay.getDate() + 1)
      }

      return days
    }
  },
  methods: {
    formatDate(date) {
      if (!date) return ''
      const d = date instanceof Date ? date : new Date(date)
      return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    },
    isToday(date) {
      const today = new Date()
      return date.toDateString() === today.toDateString()
    },
    hasRecordOnDate(date) {
      const dateStr = date.toISOString().split('T')[0]
      return this.records.some(record => {
        const startDate = record.start_date?.toDate?.() || new Date(record.start_date)
        const endDate = record.end_date ? (record.end_date?.toDate?.() || new Date(record.end_date)) : startDate

        return date >= startDate && date <= endDate
      })
    },
    isPeriodDay(date) {
      return this.records.some(record => {
        const startDate = record.start_date?.toDate?.() || new Date(record.start_date)
        const endDate = record.end_date ? (record.end_date?.toDate?.() || new Date(record.end_date)) : startDate

        startDate.setHours(0, 0, 0, 0)
        endDate.setHours(0, 0, 0, 0)
        const checkDate = new Date(date)
        checkDate.setHours(0, 0, 0, 0)

        return checkDate >= startDate && checkDate <= endDate
      })
    },
    isPredictedPeriod(date) {
      if (!this.predictions.nextPeriodDate || this.isPeriodDay(date)) return false

      const today = new Date()
      today.setHours(0, 0, 0, 0)
      if (date <= today) return false

      const nextPeriod = new Date(this.predictions.nextPeriodDate)
      nextPeriod.setHours(0, 0, 0, 0)

      const endPredicted = new Date(nextPeriod)
      endPredicted.setDate(endPredicted.getDate() + 5) // Average 5-day period

      const checkDate = new Date(date)
      checkDate.setHours(0, 0, 0, 0)

      return checkDate >= nextPeriod && checkDate <= endPredicted
    },
    isOvulationDay(date) {
      if (!this.predictions.ovulationDate) return false

      const ovDate = new Date(this.predictions.ovulationDate)
      ovDate.setHours(0, 0, 0, 0)
      const checkDate = new Date(date)
      checkDate.setHours(0, 0, 0, 0)

      return checkDate.getTime() === ovDate.getTime()
    },
    isFertileDay(date) {
      if (!this.predictions.ovulationDate) return false

      const ovDate = new Date(this.predictions.ovulationDate)
      ovDate.setHours(0, 0, 0, 0)

      const fertileStart = new Date(ovDate)
      fertileStart.setDate(fertileStart.getDate() - 5)

      const fertileEnd = new Date(ovDate)
      fertileEnd.setDate(fertileEnd.getDate() + 1)

      const checkDate = new Date(date)
      checkDate.setHours(0, 0, 0, 0)

      return checkDate >= fertileStart && checkDate <= fertileEnd && !this.isOvulationDay(date)
    },
    getDayClass(day) {
      const classes = []

      if (!day.isCurrentMonth) classes.push('other-month')
      if (day.isToday) classes.push('today')
      if (day.isPeriod) classes.push('period-day')
      if (day.isPredictedPeriod) classes.push('predicted-period-day')
      if (day.isOvulation) classes.push('ovulation-day')
      if (day.isFertile) classes.push('fertile-day')

      return classes
    }
  }
}
</script>

<style scoped>
.calendar-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 5px;
  margin-bottom: 0.5rem;
}

.weekday-header {
  text-align: center;
  font-weight: 600;
  color: #6c757d;
  font-size: 0.875rem;
  padding: 0.5rem 0;
}

.calendar-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 5px;
}

.calendar-day {
  aspect-ratio: 1;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  background: white;
}

.calendar-day:hover {
  background-color: #f8f9fa;
  transform: scale(1.05);
}

.calendar-day.other-month {
  color: #ccc;
  background-color: #f8f9fa;
}

.calendar-day.today {
  border-color: #9C27B0;
  background-color: rgba(156, 39, 176, 0.1);
  font-weight: bold;
}

.calendar-day.period-day {
  background-color: rgba(220, 53, 69, 0.2);
  border-color: #dc3545;
}

.calendar-day.predicted-period-day {
  background-color: rgba(255, 193, 203, 0.2);
  border-color: #ffb3c1;
  border-style: dashed;
}

.calendar-day.ovulation-day {
  background-color: rgba(255, 193, 7, 0.2);
  border-color: #ffc107;
}

.calendar-day.fertile-day {
  background-color: rgba(40, 167, 69, 0.15);
  border-color: #28a745;
}

.day-number {
  font-size: 1rem;
  font-weight: 500;
}

.record-indicator {
  position: absolute;
  bottom: 2px;
  right: 2px;
  font-size: 0.75rem;
}

@media (max-width: 768px) {
  .calendar-days,
  .calendar-weekdays {
    gap: 3px;
  }

  .calendar-day {
    border-width: 1px;
  }

  .day-number {
    font-size: 0.875rem;
  }

  .weekday-header {
    font-size: 0.75rem;
    padding: 0.25rem 0;
  }
}
</style>
